import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { Destination, DestinationMetadataApiClient } from '../services/DestinationsApiClient';

import { useMutation } from '@tanstack/react-query';
import { StreamingStart } from '../services/StreamingStartService';
import { TiktokApiClient } from '../services/Tiktok';
import { navigateInModal } from '../utils/navigateInModal';
import { TiktokKVStoreApiClient } from './kvstore';
import { Analytics } from '../services/AnalyticsService';
import { CreatorIneligibilityError } from '../types/errors';

function displayStreamSetupModal({ outputId }: { outputId: string }) {
  return navigateInModal({
    to: '/settings/broadcast-settings',
    title: 'Settings - StreamElements Multi-streaming',
    width: 610,
    height: 880,
    search: { output_id: outputId, mode: 'streams:start' },
  });
}

export async function connectTiktokAccount({
  authContextProvider,
  serviceEnvironment,
  streamDestination,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  streamDestination: Destination | null;
}): Promise<Destination<'tiktok'>> {
  const { isStreamingActive } = await StreamingStart.getStreamingStatus();

  const tiktokAuthMetadata = await TiktokApiClient.ensureFreshTokens(streamDestination || ({} as never));

  if (streamDestination) {
    const destinationMetadataApiClient = new DestinationMetadataApiClient({
      authContextProvider,
      serviceEnvironment,
      destinationMetadata: streamDestination,
    });

    await destinationMetadataApiClient.updateDestination({
      ...tiktokAuthMetadata,
      outputs: streamDestination.outputs.map((output) => {
        output.isEnabled = true;
        return output;
      }),
    });

    Analytics.reportMultiStreamingEvent({
      name: 'streaming_output_enabled',
      fields: [
        ['platform', 'tiktok'],
        ['streaming_outputs_added', 1],
        ['is_tiktok_streaming_eligible', true],
      ],
    });

    if (!isStreamingActive) {
      return destinationMetadataApiClient.get() as Destination<'tiktok'>;
    }

    await displayStreamSetupModal({ outputId: streamDestination.outputs[0].id });

    return destinationMetadataApiClient.get() as Destination<'tiktok'>;
  }

  const tiktokApiClient = new TiktokApiClient({
    authContextProvider,
    serviceEnvironment,
    tiktokAuthMetadata,
  });

  const { destinationMetadata: output, error } = await tiktokApiClient.createStreamingOutput({
    notifyOBS: !isStreamingActive,
  });

  let isTiktokStreamingEligible = true;

  if (error instanceof CreatorIneligibilityError) {
    isTiktokStreamingEligible = false;

    const tiktokKvStoreApiClient = new TiktokKVStoreApiClient({
      authContextProvider,
      serviceEnvironment,
    });

    await tiktokKvStoreApiClient.setApprovalStatus({
      providerId: output.id,
      status: false,
    });

    await navigateInModal({
      to: '/auth/tiktok/creator-ineligibility-notice',
      title: 'Tiktok output creation failed',
      width: 500,
      height: 240,
    });
  }

  Analytics.reportMultiStreamingEvent({
    name: 'streaming_output_enabled',
    fields: [
      ['platform', 'tiktok'],
      ['streaming_outputs_added', 1],
      ['is_tiktok_streaming_eligible', isTiktokStreamingEligible],
    ],
  });

  if (isStreamingActive && !error) {
    await displayStreamSetupModal({ outputId: output.id });
  }

  return output as Destination<'tiktok'>;
}

export function useEnableTiktokMutation({
  authContextProvider,
  serviceEnvironment,
  streamDestination,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  streamDestination: Destination | null;
}) {
  return useMutation({
    mutationKey: ['enable-tiktok', streamDestination],
    mutationFn: async () => connectTiktokAccount({ authContextProvider, serviceEnvironment, streamDestination }),
  });
}
